import { useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import toast from "react-hot-toast";

import {
  EmptyState,
  PageHeader,
  RowActions,
  StatusBadge,
  TenantGateBanner,
  rowActionsTableStyle,
  useTenantGate,
} from "../../components/ui";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  MIN_ACTIVE_REWARDS_FOR_ROLLOUT,
  POINT_REWARD_TYPE_LABELS,
  PointReward,
  countActivePointRewards,
  formatPointRewardCost,
  formatPointRewardStock,
} from "../../models/pointReward";
import {
  useGetPointRewardsQuery,
  useUpdatePointRewardMutation,
} from "../../store/services/point-rewards";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { BenefitsMetricsRefreshingIndicator } from "../Reporting/components/BenefitsMetricsWidgets";
import { DeactivatePointRewardDialog } from "./components/DeactivatePointRewardDialog";
import { PointRewardDetailDialog } from "./components/PointRewardDetailDialog";
import { PointRewardFormDialog } from "./components/PointRewardFormDialog";
import { PointsPageCard, PointsPageLoading } from "../PointRules/components/PointsPageCard";

const PAGE_TITLE = "Catálogo de canjes";
const ROLLOUT_WARNING =
  `Hay menos de ${MIN_ACTIVE_REWARDS_FOR_ROLLOUT} premios activos. ` +
  `No actives el canje de puntos para el público hasta tener al menos ${MIN_ACTIVE_REWARDS_FOR_ROLLOUT}.`;

// null = ilimitado (lo resuelve formatPointRewardStock); 0 = agotado, que se muestra como estado.
const stockBodyTemplate = (reward: PointReward) =>
  reward.stock === 0 ? (
    <StatusBadge label="Agotado" tone="error" />
  ) : (
    formatPointRewardStock(reward.stock)
  );

export const PointRewards = () => {
  const scope = useTenantScope();
  const gate = useTenantGate();
  const pointsFlag = useIsFeatureFlagEnabled("gamification_points_enabled");
  const tenantId = gate.tenantId;

  const [dialogTenantId, setDialogTenantId] = useState(tenantId);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedReward, setSelectedReward] = useState<PointReward | null>(null);
  const [rewardToDeactivate, setRewardToDeactivate] = useState<PointReward | null>(null);
  const [rewardToView, setRewardToView] = useState<PointReward | null>(null);
  const [savingRewardId, setSavingRewardId] = useState<number | null>(null);

  // Cambiar el tenant del header cierra los modales: el premio abierto era del tenant anterior.
  if (dialogTenantId !== tenantId) {
    setDialogTenantId(tenantId);
    setFormVisible(false);
    setSelectedReward(null);
    setRewardToDeactivate(null);
    setRewardToView(null);
  }

  const {
    currentData: rewards,
    isLoading,
    isFetching,
    isError,
    error: rewardsError,
  } = useGetPointRewardsQuery(tenantId ?? "", {
    skip: !gate.allowed || !pointsFlag.enabled || pointsFlag.loading || scope.loading,
  });
  const [updatePointReward] = useUpdatePointRewardMutation();

  // Solo con la query terminada y sin error: mientras carga, contar 0 activos sería un falso aviso.
  const showRolloutWarning =
    !isError &&
    rewards !== undefined &&
    countActivePointRewards(rewards) < MIN_ACTIVE_REWARDS_FOR_ROLLOUT;

  const openCreateDialog = () => {
    setSelectedReward(null);
    setFormVisible(true);
  };

  const openEditDialog = (reward: PointReward) => {
    setSelectedReward(reward);
    setFormVisible(true);
  };

  const closeFormDialog = () => setFormVisible(false);

  const handleReactivate = async (reward: PointReward) => {
    setSavingRewardId(reward.id);
    try {
      await updatePointReward({ id: reward.id, changes: { active: true } }).unwrap();
      toast.success("Premio reactivado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "No se pudo reactivar el premio");
    } finally {
      setSavingRewardId((current) => (current === reward.id ? null : current));
    }
  };

  // Mismo patrón que Activa en Reglas. Apagar pasa por el modal de baja, que avisa los QR
  // pendientes; prender reactiva directo.
  const activeBodyTemplate = (reward: PointReward) => (
    <InputSwitch
      checked={reward.active}
      disabled={savingRewardId === reward.id}
      aria-label={`${reward.active ? "Desactivar" : "Reactivar"} premio ${reward.title}`}
      onChange={() =>
        reward.active ? setRewardToDeactivate(reward) : void handleReactivate(reward)
      }
    />
  );

  const actionsBodyTemplate = (reward: PointReward) => (
    <RowActions
      actions={[
        {
          icon: "pi pi-eye",
          label: "Ver",
          severity: "info",
          onClick: () => setRewardToView(reward),
        },
        {
          icon: "pi pi-pencil",
          label: "Editar premio",
          onClick: () => openEditDialog(reward),
          // Mientras su switch se guarda, la fila todavía no tiene el estado nuevo.
          disabled: savingRewardId === reward.id,
        },
      ]}
    />
  );

  if (scope.loading) {
    return <PointsPageLoading title={PAGE_TITLE} />;
  }

  if (!gate.allowed) {
    return (
      <PointsPageCard>
        <PageHeader title={PAGE_TITLE} />
        <TenantGateBanner gate={gate} action="ver y editar su catálogo de canjes" />
      </PointsPageCard>
    );
  }

  if (pointsFlag.loading) {
    return <PointsPageLoading title={PAGE_TITLE} />;
  }

  if (!pointsFlag.enabled) {
    return (
      <PointsPageCard>
        <PageHeader title={PAGE_TITLE} />
        <div className="flex justify-content-center p-4">
          <Message severity="info" text="La sección está deshabilitada para este tenant." />
        </div>
      </PointsPageCard>
    );
  }

  return (
    <>
      <PointsPageCard>
        <PageHeader title={PAGE_TITLE} onCreate={openCreateDialog} createLabel="Crear premio" />

        <BenefitsMetricsRefreshingIndicator visible={isFetching && !isLoading} />

        {/* Con error no se muestra la tabla: "No hay premios todavía" mentiría sobre un fallo. */}
        {isError ? (
          <Message
            severity="error"
            text={getApiErrorMessage(rewardsError) ?? "No se pudieron cargar los premios."}
          />
        ) : (
          <>
            {showRolloutWarning && (
              <Message
                severity="warn"
                className="w-full justify-content-start mb-4"
                text={ROLLOUT_WARNING}
              />
            )}
            <DataTable
              style={rowActionsTableStyle("1rem", "7rem")}
              value={rewards ?? []}
              loading={isLoading}
              dataKey="id"
              // Las celdas de Activo y Acciones dependen de savingRewardId, que no es parte de la
              // fila: con el memo por defecto (compara solo rowData) quedaban bloqueadas.
              cellMemo={false}
              stripedRows
              responsiveLayout="scroll"
              emptyMessage={<EmptyState entity="premios" />}
            >
              <Column header="Título" body={(reward: PointReward) => reward.title} />
              <Column
                header="Costo"
                body={(reward: PointReward) => formatPointRewardCost(reward.pointsCost)}
              />
              <Column
                header="Tipo"
                body={(reward: PointReward) => POINT_REWARD_TYPE_LABELS[reward.rewardType]}
              />
              <Column header="Stock" body={stockBodyTemplate} />
              <Column header="Partner" body={(reward: PointReward) => reward.partnerName ?? "—"} />
              <Column
                header="Activo"
                body={activeBodyTemplate}
                alignHeader="center"
                bodyClassName="text-center"
              />
              <Column
                header="Canjes pendientes"
                body={(reward: PointReward) => reward.pendingRedemptionCount}
              />
              <Column
                header={<span className="row-actions-label">Acciones</span>}
                headerClassName="row-actions-th"
                bodyClassName="row-actions-cell"
                body={actionsBodyTemplate}
              />
            </DataTable>
          </>
        )}
      </PointsPageCard>

      {tenantId !== null && (
        <PointRewardFormDialog
          visible={formVisible}
          onHide={closeFormDialog}
          tenantId={tenantId}
          reward={selectedReward}
        />
      )}
      <PointRewardDetailDialog reward={rewardToView} onHide={() => setRewardToView(null)} />
      <DeactivatePointRewardDialog
        reward={rewardToDeactivate}
        onHide={() => setRewardToDeactivate(null)}
      />
    </>
  );
};
